import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { Layout } from "../components/Layout";
import { AvatarCropDialog } from "../components/AvatarCropDialog";
import { PushNotificationSettingsCard } from "../components/PushNotificationSettingsCard";
import { WellnessGovernanceCard } from "../components/wellness/WellnessGovernanceCard";
import { API_URL, fetchAPI } from "../lib/utils";
import {
	DOMAIN_PHYSICAL,
	DOMAIN_SHOOTING,
	hasDomain,
} from "../lib/domainAccess";
import { ShootingProfileSection } from "../components/shooting/ShootingProfileSection";
import { SportsExplainer } from "../components/sports/SportsExplainer";
import { SportsPicker } from "../components/sports/SportsPicker";
import {
	CompetitionParticipantsDialog,
	SharedAthletesBadge,
} from "../components/planning/CompetitionParticipantsDialog";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Switch } from "../components/ui/switch";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from "../components/ui/dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../components/ui/select";
import {
	User,
	Users,
	Crosshair,
	Dumbbell,
	Calendar,
	Award,
	ChevronRight,
	ChevronDown,
	Search,
	Plus,
	Trash2,
	MapPin,
	Clock,
	Save,
	Mail,
	Ruler,
	Weight,
	Cake,
	Play,
	Image,
	ExternalLink,
	Edit2,
	Zap,
	Upload,
	Pencil,
	Bell,
	HeartPulse,
} from "lucide-react";
import {
	isPushSupported,
	hasLocalPushSubscription,
	syncLocalPushSubscriptionToServer,
	subscribeUserToPush,
	unsubscribeUserFromPush,
} from "../lib/pushNotifications";
import {
	LineChart,
	Line,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
} from "recharts";
import {
	useChartColors,
	useChartTooltipStyle,
} from "../lib/useChartColors";
import { toast } from "sonner";
import {
	formatIntervalSecondsLabel,
	getDisplayEmomRounds,
	getEmomRoundLabel,
	formatEmomItemSummary,
} from "../lib/conditioningResultUtils";
import {
	getYouTubeEmbedUrl,
	getYouTubeThumbnail as getYouTubeThumb,
} from "../lib/coachUtils";

const EVENT_TYPES = [
	{ value: "gara", label: "Gara" },
	{ value: "qualificazione", label: "Qualificazione" },
	{ value: "open", label: "Open" },
	{ value: "test", label: "Test" },
	{ value: "altro", label: "Altro" },
];

const CATEGORY_LABELS = {
	strength: "Forza",
	olympic: "Olimpici",
	cardio: "Cardio",
	gymnastics: "Ginnastica",
	accessory: "Accessori",
	conditioning: "Conditioning",
};

const CATEGORY_COLORS = {
	strength: "bg-volt/20 text-volt",
	olympic: "bg-yellow-500/20 text-yellow-500",
	cardio: "bg-green-500/20 text-green-500",
	gymnastics: "bg-purple-500/20 text-purple-500",
	accessory: "bg-blue-500/20 text-blue-500",
	conditioning: "bg-orange-500/20 text-orange-500",
};

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

function getImageUrl(url) {
	if (!url) return null;
	if (url.startsWith("http")) return url;
	if (url.startsWith("/api/")) return `${backendUrl}${url}`;
	if (url.startsWith("/uploads/")) return `${backendUrl}/api${url}`;
	return url;
}

export default function Profile() {
	const chartColors = useChartColors();
	const tooltipStyle = useChartTooltipStyle();
	const { user, updateUser } = useAuth();
	const { t, language } = useLanguage();
	const userId = user?.user_id;
	const [activeTab, setActiveTab] = useState("profilo");
	const [loading, setLoading] = useState(true);

	// Profile data
	const [profileForm, setProfileForm] = useState({
		name: "",
		surname: "",
		nickname: "",
		// `sports` e' la fonte di verita'; `sport` resta come primaria derivata
		// lato server (backend/services/sports.py). Ogni sezione per-sport
		// mostra un tab per sport. Spec: docs/shooting/08-atleta-multisport.md
		sports: [],
		sport: "",
		// Classe di merito federale per disciplina (D14). Scritta anche dal
		// coach: stessa rotta, stessa guardia di autorizzazione.
		shooting_classes: {},
		performance_public: false,
		results_public: true,
		gender: "",
		weight: "",
		height: "",
		date_of_birth: "",
	});
	const [savedProfileSnapshot, setSavedProfileSnapshot] = useState(null);
	const [saving, setSaving] = useState(false);
	const [uploadingAvatar, setUploadingAvatar] = useState(false);
	const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
	const [cropImageSrc, setCropImageSrc] = useState(null);
	const [sportsOptions, setSportsOptions] = useState([]);
	const avatarInputRef = useRef(null);

	useEffect(() => {
		setAvatarLoadFailed(false);
	}, [user?.picture]);

	// Dirty tracking for profile form
	const isProfileDirty = useMemo(() => {
		if (savedProfileSnapshot === null) return false;
		return JSON.stringify(profileForm) !== savedProfileSnapshot;
	}, [profileForm, savedProfileSnapshot]);

	const confirmLeaveWithUnsavedProfile = useCallback(() => {
		if (!isProfileDirty) return true;
		return window.confirm(t("profileUnsavedChangesWarning"));
	}, [isProfileDirty, t]);

	useEffect(() => {
		if (!isProfileDirty) return undefined;

		const handleBeforeUnload = (event) => {
			event.preventDefault();
			event.returnValue = "";
		};

		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => window.removeEventListener("beforeunload", handleBeforeUnload);
	}, [isProfileDirty]);

	useEffect(() => {
		const handleDocumentLinkClick = (event) => {
			if (!isProfileDirty || event.defaultPrevented) return;
			if (
				event.button !== 0 ||
				event.metaKey ||
				event.ctrlKey ||
				event.shiftKey ||
				event.altKey
			)
				return;

			const target = event.target;
			if (!(target instanceof Element)) return;

			const link = target.closest("a[href]");
			if (!link) return;

			const rawHref = link.getAttribute("href");
			if (!rawHref || rawHref.startsWith("#")) return;
			if (link.getAttribute("target") === "_blank") return;

			const currentUrl =
				window.location.pathname +
				window.location.search +
				window.location.hash;
			const nextUrlObj = new URL(link.href, window.location.origin);
			const nextUrl = nextUrlObj.pathname + nextUrlObj.search + nextUrlObj.hash;
			if (nextUrl === currentUrl) return;

			if (!confirmLeaveWithUnsavedProfile()) {
				event.preventDefault();
				event.stopPropagation();
			}
		};

		document.addEventListener("click", handleDocumentLinkClick, true);
		return () =>
			document.removeEventListener("click", handleDocumentLinkClick, true);
	}, [isProfileDirty, confirmLeaveWithUnsavedProfile]);

	// Stats
	const [stats, setStats] = useState(null);

	// Exercise stats
	const [exerciseStats, setExerciseStats] = useState([]);
	const [exercises, setExercises] = useState([]);
	const [exerciseSearch, setExerciseSearch] = useState("");
	const [categoryFilter, setCategoryFilter] = useState("all");
	const [selectedExerciseId, setSelectedExerciseId] = useState(null);
	const [exerciseHistory, setExerciseHistory] = useState([]);
	const [historyLoading, setHistoryLoading] = useState(false);
	const [mediaExercise, setMediaExercise] = useState(null);
	const [conditioningPreviewExercise, setConditioningPreviewExercise] =
		useState(null);

	// Calendar
	const [competitions, setCompetitions] = useState([]);
	// Mesocicli configurati dal coach: sola lettura lato atleta.
	const [mesocycles, setMesocycles] = useState([]);
	const [isEventDialogOpen, setIsEventDialogOpen] = useState(false);
	const [editingEvent, setEditingEvent] = useState(null);
	const [newEvent, setNewEvent] = useState({
		name: "",
		date: "",
		location: "",
		event_type: "gara",
		notes: "",
	});
	// Races already in the calendar of athletes sharing a coach: used to
	// autocomplete the form so the same event is not typed twice.
	const [eventSuggestions, setEventSuggestions] = useState([]);
	const [appliedSuggestionKey, setAppliedSuggestionKey] = useState(null);
	// Races of the *other* athletes of my coaches (other sports / groups included).
	// Discovery only, read-only, and loaded lazily: it is an extra query that
	// nobody pays for until the section is opened.
	const [otherEvents, setOtherEvents] = useState([]);
	const [otherEventsOpen, setOtherEventsOpen] = useState(false);
	const [otherEventsLoading, setOtherEventsLoading] = useState(false);
	const [otherEventsLoaded, setOtherEventsLoaded] = useState(false);
	// Teammates attending the same race, shown when the "N athletes" pill is tapped.
	const [participantsEvent, setParticipantsEvent] = useState(null);
	const [participants, setParticipants] = useState([]);
	const [participantsLoading, setParticipantsLoading] = useState(false);
	const [assignments, setAssignments] = useState({ coaches: [], groups: [] });
	const [selectedAssignmentGroup, setSelectedAssignmentGroup] = useState(null);

	// Notification preferences
	const [notifPrefs, setNotifPrefs] = useState({
		email_new_program: false,
		email_new_message: false,
		email_workout_reminder: false,
		push_enabled: false,
		push_new_message: true,
		push_new_program_and_week: true,
		push_ever_enabled: false,
	});
	const [savingNotifs, setSavingNotifs] = useState(false);
	const [pushSupported, setPushSupported] = useState(false);
	const [pushServerConfigured, setPushServerConfigured] = useState(false);
	const [pushBusy, setPushBusy] = useState(false);

	const getSportLabel = (sportValue) => {
		if (!sportValue) return "";
		const found = sportsOptions.find((s) => s.value === sportValue);
		if (found?.label) return found.label;
		return String(sportValue)
			.replace(/_/g, " ")
			.replace(/\b\w/g, (c) => c.toUpperCase());
	};

	const getPersonInitials = (nameValue) => {
		const normalized = String(nameValue || "").trim();
		if (!normalized) return "?";
		const parts = normalized.split(/\s+/).slice(0, 2);
		return parts.map((part) => part[0]?.toUpperCase() || "").join("") || "?";
	};

	// Step 1: file chosen → read as data URL → open crop dialog
	const handleAvatarUpload = (event) => {
		const file = event.target.files?.[0];
		event.target.value = "";
		if (!file || !userId) return;

		if (!file.type.startsWith("image/")) {
			toast.error(t("invalidImageType"));
			return;
		}

		const reader = new FileReader();
		reader.onload = () => setCropImageSrc(reader.result);
		reader.readAsDataURL(file);
	};

	// Step 2: crop confirmed → compress blob → upload
	const handleCropConfirm = useCallback(
		async (blob) => {
			setCropImageSrc(null);
			if (!userId) return;
			setUploadingAvatar(true);
			try {
				const formData = new FormData();
				formData.append("file", blob, "avatar.jpg");

				const token = localStorage.getItem("session_token") || "";
				const response = await fetch(`${API_URL}/api/users/${userId}/picture`, {
					method: "POST",
					credentials: "include",
					headers: token ? { Authorization: `Bearer ${token}` } : undefined,
					body: formData,
				});

				const data = await response
					.json()
					.catch(() => ({ detail: t("uploadError") }));

				if (!response.ok) {
					throw new Error(data.detail || t("uploadError"));
				}

				const pictureUrl = data?.picture || data?.user?.picture || null;
				if (pictureUrl) {
					updateUser({ picture: pictureUrl });
				}
				toast.success(t("avatarUpdated"));
			} catch (error) {
				toast.error(`${t("uploadError")}: ${error.message}`);
			} finally {
				setUploadingAvatar(false);
			}
		},
		[userId, t, updateUser],
	);

	useEffect(() => {
		if (!userId) return;

		let cancelled = false;

		const loadAllData = async () => {
			try {
				const [
					statsData,
					exStats,
					exList,
					events,
					mesocycleData,
					sportsData,
					userData,
					assignmentsData,
				] = await Promise.all([
					fetchAPI("/profile/stats").catch(() => null),
					fetchAPI("/profile/exercise-stats").catch(() => []),
					fetchAPI("/exercises").catch(() => []),
					fetchAPI("/profile/competitions").catch(() => []),
					fetchAPI("/mesocycles/mine").catch(() => []),
					fetchAPI("/users/sports/list").catch(() => []),
					fetchAPI(`/users/${userId}`).catch(() => null),
					fetchAPI("/profile/assignments").catch(() => ({
						coaches: [],
						groups: [],
					})),
				]);

				if (cancelled) return;

				setStats(statsData);
				setExerciseStats(exStats || []);
				setExercises(exList || []);
				setCompetitions(events || []);
				setMesocycles(Array.isArray(mesocycleData) ? mesocycleData : []);
				setSportsOptions(
					Array.isArray(sportsData)
						? sportsData.map((s) => ({
								value: s.value,
								label: s.label || s.value,
								// Serve a distinguere le discipline di tiro: senza,
								// il tab "Tiro" non sa quali select di classe mostrare.
								families: s.families || [],
								// Sport solo «legati» (disattivati, o di un coach che
								// non ti segue più): l'etichetta sì, la proposta no.
								selectable: s.selectable !== false,
							}))
						: [],
				);
				setAssignments({
					coaches: Array.isArray(assignmentsData?.coaches)
						? assignmentsData.coaches
						: [],
					groups: Array.isArray(assignmentsData?.groups)
						? assignmentsData.groups
						: [],
				});

				// Init profile form only from initial server payload for this user
				const profileUser = userData || {};
				const initialForm = {
					name: profileUser?.name ?? "",
					surname: profileUser?.surname ?? "",
					nickname: profileUser?.nickname ?? "",
					sports:
						Array.isArray(profileUser?.sports) && profileUser.sports.length
							? profileUser.sports
							: profileUser?.sport
								? [profileUser.sport]
								: [],
					sport: profileUser?.sport ?? "",
					shooting_classes: profileUser?.shooting_classes ?? {},
					performance_public: !!profileUser?.performance_public,
					results_public: profileUser?.results_public !== false,
					gender: profileUser?.gender ?? "",
					weight: profileUser?.weight ?? "",
					height: profileUser?.height ?? "",
					date_of_birth: profileUser?.date_of_birth ?? "",
				};
				setProfileForm(initialForm);
				setSavedProfileSnapshot(JSON.stringify(initialForm));
			} catch (error) {
				if (!cancelled) {
					console.error("Failed to load profile:", error);
				}
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		};

		loadAllData();

		return () => {
			cancelled = true;
		};
	}, [userId]);

	/* ── Profile save ── */
	const handleSaveProfile = async () => {
		setSaving(true);
		try {
			const body = {
				name: profileForm.name,
				surname: profileForm.surname || null,
				nickname: profileForm.nickname || null,
				// Si invia solo `sports`: il server deriva `sport` (la primaria)
				// e non va mai scritto a mano, altrimenti i due divergono.
				sports: profileForm.sports || [],
				shooting_classes: profileForm.shooting_classes || {},
				performance_public: !!profileForm.performance_public,
				results_public: profileForm.results_public !== false,
				gender: profileForm.gender || null,
				weight: profileForm.weight ? parseFloat(profileForm.weight) : null,
				height: profileForm.height ? parseFloat(profileForm.height) : null,
				date_of_birth: profileForm.date_of_birth || null,
			};
			const updated = await fetchAPI(`/users/${user.user_id}`, {
				method: "PUT",
				body: JSON.stringify(body),
			});
			// La risposta e' il documento grezzo, non la sessione: niente
			// `domains` (aggiungere IPSC non faceva comparire la scheda Tiro fino
			// al ricaricamento) e `role` e' quello salvato, non la modalita'
			// attiva (un account doppio in modalita' coach tornava atleta). Si
			// rilegge /auth/me; se fallisce si fondono i soli dati del profilo,
			// senza mai azzerare l'utente.
			try {
				updateUser(await fetchAPI("/auth/me"));
			} catch {
				const {
					role: _role,
					active_role: _activeRole,
					base_role: _baseRole,
					coach_enabled: _coachEnabled,
					is_admin: _isAdmin,
					...profileFields
				} = updated || {};
				updateUser(profileFields);
			}
			setSavedProfileSnapshot(JSON.stringify(profileForm));
			toast.success(t("profileUpdated"));
		} catch (error) {
			toast.error(`${t("error")}: ${error.message}`);
		} finally {
			setSaving(false);
		}
	};

	/* ── Exercise detail ── */
	const handleSelectExercise = async (exerciseId) => {
		if (selectedExerciseId === exerciseId) {
			setSelectedExerciseId(null);
			return;
		}
		setSelectedExerciseId(exerciseId);
		setHistoryLoading(true);
		try {
			const history = await fetchAPI(`/results/history/${exerciseId}`);
			setExerciseHistory(history || []);
		} catch {
			setExerciseHistory([]);
		} finally {
			setHistoryLoading(false);
		}
	};

	/* ── Competition handlers ── */
	const loadEventSuggestions = async () => {
		try {
			const data = await fetchAPI("/profile/competitions/suggestions");
			setEventSuggestions(Array.isArray(data) ? data : []);
		} catch {
			setEventSuggestions([]);
		}
	};

	const openNewEventDialog = () => {
		setEditingEvent(null);
		setNewEvent({
			name: "",
			date: "",
			location: "",
			event_type: "gara",
			notes: "",
		});
		setAppliedSuggestionKey(null);
		setIsEventDialogOpen(true);
		loadEventSuggestions();
	};

	// Own race: the roster lives behind an endpoint scoped to the athletes sharing
	// a coach with me. Suggestions already carry theirs, so they skip the fetch.
	const openEventParticipants = async (event) => {
		setParticipantsEvent(event);
		setParticipants([]);
		setParticipantsLoading(true);
		try {
			const data = await fetchAPI(
				`/profile/competitions/${event.event_id}/participants`,
			);
			setParticipants(data?.athletes || []);
		} catch {
			toast.error(t("competitionParticipantsError"));
		} finally {
			setParticipantsLoading(false);
		}
	};

	const loadOtherEvents = async () => {
		setOtherEventsLoading(true);
		try {
			const data = await fetchAPI("/profile/competitions/others");
			setOtherEvents(Array.isArray(data) ? data : []);
			setOtherEventsLoaded(true);
		} catch {
			toast.error(t("competitionOthersError"));
		} finally {
			setOtherEventsLoading(false);
		}
	};

	const toggleOtherEvents = () => {
		const next = !otherEventsOpen;
		setOtherEventsOpen(next);
		if (next && !otherEventsLoaded) loadOtherEvents();
	};

	// My own calendar changed: a race I just joined must leave the "others" list.
	const invalidateOtherEvents = () => {
		if (otherEventsOpen) loadOtherEvents();
		else setOtherEventsLoaded(false);
	};

	// Reuses the "new race" dialog, prefilled: the race is already described by
	// someone else, the athlete only confirms it and keeps their personal fields.
	const addOtherEventToCalendar = (row) => {
		setEditingEvent(null);
		setNewEvent({
			name: row.name || "",
			date: row.date || "",
			end_date: row.end_date || "",
			location: row.location || "",
			event_type: row.event_type || "gara",
			discipline: row.discipline || "",
			notes: "",
		});
		setAppliedSuggestionKey(row.group_key);
		setIsEventDialogOpen(true);
		loadEventSuggestions();
	};

	const openSuggestionParticipants = (suggestion) => {
		setParticipantsEvent(suggestion);
		setParticipants(suggestion.athletes || []);
		setParticipantsLoading(false);
	};

	const applyEventSuggestion = (suggestion) => {
		setNewEvent((prev) => ({
			...prev,
			name: suggestion.name || "",
			date: suggestion.date || "",
			end_date: suggestion.end_date || "",
			location: suggestion.location || "",
			event_type: suggestion.event_type || "gara",
			discipline: suggestion.discipline || "",
		}));
		setAppliedSuggestionKey(suggestion.group_key);
	};

	const handleCreateEvent = async () => {
		if (!newEvent.name || !newEvent.date) return;
		try {
			await fetchAPI("/profile/competitions", {
				method: "POST",
				body: JSON.stringify(newEvent),
			});
			toast.success(t("eventAdded"));
			setIsEventDialogOpen(false);
			setNewEvent({
				name: "",
				date: "",
				location: "",
				event_type: "gara",
				notes: "",
			});
			const events = await fetchAPI("/profile/competitions");
			setCompetitions(events || []);
			invalidateOtherEvents();
		} catch (error) {
			toast.error(`${t("error")}: ${error.message}`);
		}
	};

	const handleDeleteEvent = async (eventId) => {
		if (!window.confirm("Eliminare questo evento?")) return;
		try {
			await fetchAPI(`/profile/competitions/${eventId}`, {
				method: "DELETE",
			});
			setCompetitions((prev) => prev.filter((e) => e.event_id !== eventId));
			invalidateOtherEvents();
			toast.success(t("eventDeleted"));
		} catch (error) {
			toast.error(`${t("error")}: ${error.message}`);
		}
	};

	const openEditEvent = (event) => {
		setEditingEvent(event);
		setNewEvent({
			name: event.name || "",
			date: event.date || "",
			location: event.location || "",
			event_type: event.event_type || "gara",
			notes: event.notes || "",
			// preserve coach-managed advanced fields so athlete edits don't wipe them
			end_date: event.end_date || "",
			discipline: event.discipline || "",
			priority: event.priority || "",
			category: event.category || "",
			status: event.status || "",
			result_summary: event.result_summary || "",
		});
		setIsEventDialogOpen(true);
	};

	const handleUpdateEvent = async () => {
		if (!newEvent.name || !newEvent.date || !editingEvent) return;
		try {
			await fetchAPI(`/profile/competitions/${editingEvent.event_id}`, {
				method: "PUT",
				body: JSON.stringify(newEvent),
			});
			toast.success(t("eventUpdated"));
			setIsEventDialogOpen(false);
			setEditingEvent(null);
			setNewEvent({
				name: "",
				date: "",
				location: "",
				event_type: "gara",
				notes: "",
			});
			const events = await fetchAPI("/profile/competitions");
			setCompetitions(events || []);
		} catch (error) {
			toast.error(`${t("error")}: ${error.message}`);
		}
	};

	const handleLeaveTrainingGroup = async (group) => {
		if (!group?.group_id) return;
		if (!window.confirm(t("leaveTrainingGroupConfirm"))) return;

		try {
			await fetchAPI(`/profile/assignments/groups/${group.group_id}/leave`, {
				method: "POST",
			});

			const assignmentsData = await fetchAPI("/profile/assignments").catch(
				() => ({
					coaches: [],
					groups: [],
				}),
			);
			setAssignments({
				coaches: Array.isArray(assignmentsData?.coaches)
					? assignmentsData.coaches
					: [],
				groups: Array.isArray(assignmentsData?.groups)
					? assignmentsData.groups
					: [],
			});
			setSelectedAssignmentGroup(null);
			toast.success(t("leftTrainingGroupSuccess"));
		} catch (error) {
			toast.error(`${t("leaveTrainingGroupError")}: ${error.message}`);
		}
	};

	/* ── Derived data ── */
	const prs = stats?.personal_records || {};
	const topPRs = Object.entries(prs)
		.sort(([, a], [, b]) => (b.estimated_1rm || 0) - (a.estimated_1rm || 0))
		.slice(0, 10);

	// Merge exercise stats with exercise library to get category
	// Only show conditioning exercises linked to library (not manual/daily ones)
	const enrichedExerciseStats = useMemo(() => {
		const exMap = {};
		exercises.forEach((e) => (exMap[e.exercise_id] = e));
		return exerciseStats
			.filter((s) => {
				// Exclude conditioning results not linked to library
				if (s.exercise_type === "conditioning" && !s.library_conditioning_id) {
					return false;
				}
				return true;
			})
			.map((s) => ({
				...s,
				category:
					s.exercise_type === "conditioning"
						? "conditioning"
						: exMap[s.exercise_id]?.category || "strength",
				video_url: exMap[s.exercise_id]?.video_url || null,
				image_url: exMap[s.exercise_id]?.image_url || null,
				description: exMap[s.exercise_id]?.description || null,
			}));
	}, [exerciseStats, exercises]);

	// Available categories (for filter) — conditioning last
	const CATEGORY_ORDER = [
		"strength",
		"olympic",
		"cardio",
		"gymnastics",
		"accessory",
		"conditioning",
	];
	const availableCategories = useMemo(() => {
		const cats = new Set();
		enrichedExerciseStats.forEach((s) => cats.add(s.category || "strength"));
		const sorted = Array.from(cats).sort((a, b) => {
			const ia = CATEGORY_ORDER.indexOf(a);
			const ib = CATEGORY_ORDER.indexOf(b);
			return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
		});
		return ["all", ...sorted];
	}, [enrichedExerciseStats]);

	// Group by category
	const exercisesByCategory = useMemo(() => {
		let filtered = enrichedExerciseStats;
		if (categoryFilter !== "all") {
			filtered = filtered.filter(
				(s) => (s.category || "strength") === categoryFilter,
			);
		}
		if (exerciseSearch) {
			filtered = filtered.filter((s) =>
				s.exercise_name?.toLowerCase().includes(exerciseSearch.toLowerCase()),
			);
		}

		const grouped = {};
		filtered.forEach((s) => {
			const cat = s.category || "strength";
			if (!grouped[cat]) grouped[cat] = [];
			grouped[cat].push(s);
		});
		return grouped;
	}, [enrichedExerciseStats, exerciseSearch, categoryFilter]);

	// Chart data for selected exercise
	const chartData = useMemo(() => {
		return exerciseHistory
			.map((r) => {
				const weight = Number(r.raw_weight ?? r.weight_kg ?? r.value);
				const reps = Number(r.raw_reps ?? r.reps);
				const providedEstimated1rm = Number(r.estimated_1rm);
				const hasWeight = Number.isFinite(weight) && weight > 0;
				const hasProvidedEstimated =
					Number.isFinite(providedEstimated1rm) && providedEstimated1rm > 0;
				const estimated1rm = hasProvidedEstimated
					? providedEstimated1rm
					: hasWeight && Number.isFinite(reps) && reps > 0
						? Math.round(weight * (1 + reps / 30) * 10) / 10
						: null;

				return {
					date: r.date?.substring(5, 10),
					weight: hasWeight ? weight : null,
					estimated1rm,
				};
			})
			.filter((r) => Number.isFinite(r.weight) && r.weight > 0)
			.slice(0, 20)
			.reverse();
	}, [exerciseHistory]);

	const getNumericConditioningValue = (result) => {
		if (typeof result?.score === "number") return result.score;
		if (typeof result?.reps === "number") return result.reps;
		if (result?.reps_text !== undefined && result?.reps_text !== null) {
			const text = String(result.reps_text).trim().replace(",", ".");
			if (/^-?\d+(\.\d+)?$/.test(text)) return Number(text);
		}
		return null;
	};

	const formatHistorySummary = (result) => {
		if (!result) return "";

		const hasSetRows = Array.isArray(result.result_set_rows)
			? result.result_set_rows.some(
					(row) => row?.weight_kg || row?.reps || row?.reps_text || row?.rpe,
				)
			: false;
		if (hasSetRows) {
			const compactRows = result.result_set_rows
				.map((row) => {
					const repsValue = row?.reps_text || row?.reps;
					const parts = [];
					if (row?.weight_kg) parts.push(`${row.weight_kg}kg`);
					if (repsValue) parts.push(`x${repsValue}`);
					if (row?.rpe) parts.push(`RPE ${row.rpe}`);
					return parts.join(" ");
				})
				.filter(Boolean);
			if (compactRows.length > 0) return compactRows.join(" · ");
		}

		const hasSupersetDetails = Array.isArray(result.superset_details)
			? result.superset_details.some(
					(detail) =>
						detail?.exercise_name ||
						detail?.weight_kg ||
						detail?.reps ||
						detail?.rpe,
				)
			: false;
		if (hasSupersetDetails) {
			const compactDetails = result.superset_details
				.map((detail, idx) => {
					const parts = [];
					if (detail?.exercise_name) parts.push(detail.exercise_name);
					if (detail?.weight_kg) parts.push(`${detail.weight_kg}kg`);
					if (detail?.reps) parts.push(`x${detail.reps}`);
					if (detail?.rpe) parts.push(`RPE ${detail.rpe}`);
					if (parts.length === 0) return "";
					return `${String.fromCharCode(65 + idx)}: ${parts.join(" ")}`;
				})
				.filter(Boolean);
			if (compactDetails.length > 0) return compactDetails.join(" · ");
		}

		if (result.weight_kg && (result.reps || result.reps_text)) {
			return `${result.weight_kg}kg x ${result.reps_text || result.reps}`;
		}
		if (result.weight_kg) return `${result.weight_kg}kg`;
		if (
			Array.isArray(result.interval_scores) &&
			result.interval_scores.length > 0
		)
			return `Ripetute: ${result.interval_scores.join(" · ")}`;
		if (result.reps_text) return String(result.reps_text);
		if (result.reps) return `${result.reps} reps`;
		if (result.score) return `${result.score} reps`;
		if (result.time_seconds) {
			return `${Math.floor(result.time_seconds / 60)}:${String(
				result.time_seconds % 60,
			).padStart(2, "0")}`;
		}
		return "✓";
	};

	// Calendar split
	const today = new Date().toISOString().split("T")[0];
	// Suggestions matching what is being typed; hidden once one is applied.
	const visibleEventSuggestions = useMemo(() => {
		if (editingEvent || appliedSuggestionKey) return [];
		const term = (newEvent.name || "").trim().toLowerCase();
		const list = term
			? eventSuggestions.filter((suggestion) =>
					`${suggestion.name || ""} ${suggestion.location || ""}`
						.toLowerCase()
						.includes(term),
				)
			: eventSuggestions;
		return list.slice(0, 5);
	}, [eventSuggestions, newEvent.name, editingEvent, appliedSuggestionKey]);

	const upcomingEvents = competitions.filter((e) => e.date >= today);
	const pastEvents = competitions.filter((e) => e.date < today);

	// Mesocicli configurati dal coach, arricchiti per la sola lettura atleta:
	// durata in settimane, stato (in corso/futuro/concluso) e avanzamento.
	// I conclusi restano in coda; attivi e futuri salgono per data d'inizio.
	const mesocycleCards = useMemo(() => {
		const toTs = (d) => new Date(`${d}T00:00:00`).getTime();
		const dayMs = 1000 * 60 * 60 * 24;
		const nowTs = toTs(today);
		return (mesocycles || [])
			.filter((m) => m?.start_date && m?.end_date)
			.map((m) => {
				const startTs = toTs(m.start_date);
				const endTs = toTs(m.end_date);
				const totalDays = Math.max(
					1,
					Math.round((endTs - startTs) / dayMs) + 1,
				);
				const weeks = Math.max(1, Math.ceil(totalDays / 7));
				let status = "upcoming";
				if (nowTs > endTs) status = "completed";
				else if (nowTs >= startTs) status = "current";
				const elapsedDays = Math.min(
					totalDays,
					Math.max(0, Math.round((nowTs - startTs) / dayMs) + 1),
				);
				const progress =
					status === "completed"
						? 100
						: status === "current"
							? Math.round((elapsedDays / totalDays) * 100)
							: 0;
				const daysToStart = Math.ceil((startTs - nowTs) / dayMs);
				return { ...m, weeks, status, progress, daysToStart };
			})
			.sort((a, b) => {
				const rank = { current: 0, upcoming: 1, completed: 2 };
				if (rank[a.status] !== rank[b.status])
					return rank[a.status] - rank[b.status];
				return a.start_date.localeCompare(b.start_date);
			});
	}, [mesocycles, today]);

	useEffect(() => {
		setPushSupported(isPushSupported());
		fetchAPI("/users/me/push-config")
			.then((cfg) => setPushServerConfigured(!!cfg?.push_available))
			.catch(() => setPushServerConfigured(false));
		Promise.all([
			fetchAPI("/users/me/notifications"),
			hasLocalPushSubscription(),
		])
			.then(async ([prefs, localPushSubscribed]) => {
				if (localPushSubscribed && !prefs?.push_enabled) {
					await syncLocalPushSubscriptionToServer().catch(() => {});
				}
				const effectivePushEnabled =
					!!prefs?.push_enabled || !!localPushSubscribed;
				return setNotifPrefs((prev) => ({
					...prev,
					...prefs,
					push_enabled: effectivePushEnabled,
					push_ever_enabled: !!prefs?.push_ever_enabled || effectivePushEnabled,
					email_new_program: false,
					email_new_message: false,
					email_workout_reminder: false,
				}));
			})
			.then(() => undefined)
			.catch((e) => {
				console.error(
					"Error loading notification prefs in Profile:",
					e?.status,
					e?.detail || e?.message,
				);
			});
	}, []);

	const saveNotifPrefs = async (newPrefs) => {
		setSavingNotifs(true);
		const payload = {
			...newPrefs,
			email_new_program: false,
			email_new_message: false,
			email_workout_reminder: false,
		};
		try {
			await fetchAPI("/users/me/notifications", {
				method: "PUT",
				body: JSON.stringify(payload),
			});
			setNotifPrefs(payload);
			window.dispatchEvent(new Event("push-onboarding-updated"));
			toast.success(
				language === "it" ? "Preferenze salvate!" : "Preferences saved!",
			);
		} catch {
			toast.error(language === "it" ? "Errore salvataggio" : "Save error");
		} finally {
			setSavingNotifs(false);
		}
	};

	const handleNotifChange = (key, value) => {
		saveNotifPrefs({ ...notifPrefs, [key]: value });
	};

	const hasPushStateMismatch = async (enabled) => {
		const [serverPrefs, localPushSubscribed] = await Promise.all([
			fetchAPI("/users/me/notifications").catch(() => null),
			hasLocalPushSubscription().catch(() => false),
		]);

		if (enabled) {
			return !(!!serverPrefs?.push_enabled || !!localPushSubscribed);
		}

		// Consider disabled if either server preference is off OR local subscription is gone.
		return !!serverPrefs?.push_enabled && !!localPushSubscribed;
	};

	const handlePushEnabledChange = async (enabled) => {
		if (!pushSupported) {
			toast.error(
				language === "it"
					? "Push non supportate su questo browser"
					: "Push not supported on this browser",
			);
			return;
		}
		if (!pushServerConfigured) {
			toast.error(
				language === "it"
					? "Push non configurate sul server"
					: "Push not configured on server",
			);
			return;
		}
		setPushBusy(true);
		let subscriptionError = null;

		// Try to update subscription, but don't fail the whole operation if it fails
		if (enabled) {
			try {
				const localPushSubscribed = await hasLocalPushSubscription();
				if (!localPushSubscribed) {
					await subscribeUserToPush();
				} else {
					await syncLocalPushSubscriptionToServer().catch(() => {});
				}
			} catch (e) {
				subscriptionError = e;
				console.warn(
					"Subscription failed but will still save preference:",
					subscriptionError,
				);
			}
		} else {
			try {
				await unsubscribeUserFromPush();
			} catch (e) {
				subscriptionError = e;
				console.warn(
					"Unsubscribe failed but will still save preference:",
					subscriptionError,
				);
			}
		}

		// Always save preferences regardless of subscription result
		try {
			await saveNotifPrefs({
				...notifPrefs,
				push_enabled: enabled,
				push_ever_enabled: !!notifPrefs.push_ever_enabled || enabled,
			});
			// Show warning only if final effective state is actually inconsistent.
			if (subscriptionError && (await hasPushStateMismatch(enabled))) {
				toast.info(
					language === "it"
						? "Preferenza salvata ma la registrazione della notifica ha avuto problemi. Riprova se necessario."
						: "Preference saved but notification registration failed. Please retry if needed.",
				);
			} else if (subscriptionError) {
				console.warn(
					"Transient push subscription error ignored because effective state is correct.",
					subscriptionError,
				);
			}
		} catch (e) {
			toast.error(
				e?.message ||
					(language === "it"
						? "Impossibile aggiornare le notifiche push"
						: "Could not update push"),
			);
		} finally {
			setPushBusy(false);
		}
	};

	const handleSendPushTest = async () => {
		if (!pushSupported || !pushServerConfigured) {
			toast.error(
				language === "it"
					? "Push non disponibili su questo dispositivo/server"
					: "Push not available on this device/server",
			);
			return;
		}
		if (!notifPrefs.push_enabled) {
			toast.error(
				language === "it"
					? "Attiva prima le notifiche push"
					: "Enable push notifications first",
			);
			return;
		}

		setPushBusy(true);
		try {
			await fetchAPI("/users/me/push-test", { method: "POST" });
			toast.success(
				language === "it"
					? "Notifica di test inviata"
					: "Test notification sent",
			);
		} catch (e) {
			const detail = e?.detail;
			if (detail && typeof detail === "object") {
				const firstFailure = Array.isArray(detail.failure_examples)
					? detail.failure_examples[0]
					: null;
				const statusCode = firstFailure?.status_code;
				const reason = firstFailure?.reason || detail.reason;
				if (statusCode === 403 || reason === "webpush_exception") {
					toast.error(
						language === "it"
							? "Invio push rifiutato dal provider (controllare chiavi VAPID backend)."
							: "Push provider rejected delivery (check backend VAPID keys).",
					);
				} else if (reason === "subscription_gone") {
					toast.error(
						language === "it"
							? "Subscription push non valida: disattiva e riattiva le notifiche push."
							: "Push subscription invalid: disable and re-enable push notifications.",
					);
				} else {
					toast.error(
						language === "it"
							? "Invio notifica test fallito. Controlla i log backend per il dettaglio."
							: "Test push failed. Check backend logs for details.",
					);
				}
				return;
			}
			toast.error(
				e?.message ||
					(language === "it"
						? "Invio notifica test fallito"
						: "Test push failed"),
			);
		} finally {
			setPushBusy(false);
		}
	};

	const tabs = [
		{
			key: "profilo",
			label: t("profile"),
			shortLabel: t("profile"),
			icon: User,
		},
		{
			key: "esercizi",
			// PR e massimali: preparazione fisica. Un atleta di solo tiro non ha
			// nulla da vederci finche' non esiste lo storico drill (Fase 4).
			domain: DOMAIN_PHYSICAL,
			label: t("exercises"),
			shortLabel: t("exercises"),
			icon: Dumbbell,
		},
		{
			key: "tiro",
			domain: DOMAIN_SHOOTING,
			label: language === "it" ? "Tiro" : "Shooting",
			shortLabel: language === "it" ? "Tiro" : "Shooting",
			icon: Crosshair,
		},
		{
			key: "calendario",
			label: t("competitionCalendarTab"),
			shortLabel: t("competitionCalendarTabShort"),
			icon: Calendar,
		},
		{
			key: "assegnazioni",
			label: t("assignmentsTab"),
			shortLabel: t("assignmentsTabShort"),
			icon: Users,
		},
		{
			key: "notifiche",
			label: language === "it" ? "Notifiche" : "Notifications",
			shortLabel: language === "it" ? "Notifiche" : "Notifications",
			icon: Bell,
		},
		...(user?.role === "athlete"
			? [
					{
						key: "salute",
						label: t("wellnessHealthTab"),
						shortLabel: t("wellnessHealthTab"),
						icon: HeartPulse,
					},
				]
			: []),
	];

	// Practice-domain gating (lib/domainAccess.js). Voci senza `domain` restano
	// visibili a tutti; `user.domains` vuoto significa nessuna restrizione.
	const visibleTabs = tabs.filter(
		(tab) => !tab.domain || hasDomain(user, tab.domain),
	);

	// Se il tab attivo e' stato nascosto (l'admin ha appena tolto un ambito),
	// si torna al profilo invece di restare su un contenuto irraggiungibile.
	// La dipendenza e' il booleano, non `visibleTabs`: l'array si ricrea a ogni
	// render e farebbe girare l'effetto ogni volta.
	const activeTabHidden = !visibleTabs.some((tab) => tab.key === activeTab);
	useEffect(() => {
		if (activeTabHidden) setActiveTab("profilo");
	}, [activeTabHidden]);

	// Il primo sport selezionato diventa la primaria lato server, quindi
	// l'ordine di selezione conta: e' il tab aperto di default sul profilo
	// prestativo. Per questo si appende in coda invece di riordinare.
	// Le discipline di tiro fra quelle praticate: sono queste a generare una
	// select di classe di merito e a popolare il selettore disciplina dell'arma.
	const shootingSportsOptions = useMemo(
		() =>
			sportsOptions.filter(
				(sport) =>
					(profileForm.sports || []).includes(sport.value) &&
					(sport.families || []).includes(DOMAIN_SHOOTING),
			),
		[sportsOptions, profileForm.sports],
	);

	const handleMainTabChange = (nextTab) => {
		if (nextTab === activeTab) return;
		const leavingProfileTab = activeTab === "profilo" && nextTab !== "profilo";
		if (leavingProfileTab) {
			if (!profileForm.gender && !window.confirm(t("genderRequiredWarning")))
				return;
			if (!confirmLeaveWithUnsavedProfile()) return;
		}
		setActiveTab(nextTab);
	};

	if (loading) {
		return (
			<Layout>
				<div className="flex items-center justify-center min-h-[60vh]">
					<div className="spinner"></div>
				</div>
			</Layout>
		);
	}

	return (
		<Layout>
			<div
				className="max-w-2xl mx-auto px-4 py-5 pb-28 overflow-x-hidden"
				data-testid="profile-page"
			>
				{/* Header — compact */}
				<div className="flex items-center gap-4 mb-5">
					<div className="relative w-14 h-14 shrink-0">
						<div className="w-14 h-14 bg-volt rounded-sm flex items-center justify-center overflow-hidden">
							{user?.picture && !avatarLoadFailed ? (
								<img
									src={getImageUrl(user.picture) || user.picture}
									alt=""
									className="w-14 h-14 rounded-sm object-cover"
									onError={() => setAvatarLoadFailed(true)}
								/>
							) : (
								<span className="font-heading text-2xl text-white">
									{user?.name?.charAt(0).toUpperCase()}
								</span>
							)}
						</div>
						<input
							ref={avatarInputRef}
							type="file"
							accept="image/png,image/jpeg,image/webp,image/gif"
							onChange={handleAvatarUpload}
							className="hidden"
						/>
						<button
							type="button"
							onClick={() => avatarInputRef.current?.click()}
							disabled={uploadingAvatar}
							className="absolute -bottom-1 -right-1 h-6 w-6 rounded-sm bg-volt border border-background flex items-center justify-center text-white disabled:opacity-60"
							title={t("uploadAvatar")}
						>
							{user?.picture ? (
								<Pencil className="w-3 h-3" />
							) : (
								<Upload className="w-3 h-3" />
							)}
						</button>
					</div>
					<div className="min-w-0">
						<h1 className="font-heading text-2xl truncate">{user?.name}</h1>
						<div className="flex items-center gap-2 mt-0.5">
							{user?.sport && (
								<Badge className="bg-volt/20 text-volt border-0 text-[10px]">
									{getSportLabel(user.sport)}
								</Badge>
							)}
							{/* Workout count removed per UX request */}
						</div>
						{uploadingAvatar && (
							<p className="text-[10px] text-muted-foreground mt-1 uppercase tracking-wider">
								{t("uploadingAvatar")}
							</p>
						)}
					</div>
				</div>

				{/* Keep all profile sections visible without horizontal scrolling. */}
				<div
					className={`grid ${
						user?.role === "athlete"
							? "grid-cols-4 sm:grid-cols-7"
							: "grid-cols-3 sm:grid-cols-6"
					} gap-1 bg-secondary p-1 rounded-sm mb-3`}
				>
					{visibleTabs.map((tab) => {
						const Icon = tab.icon;
						return (
							<button
								key={tab.key}
								onClick={() => handleMainTabChange(tab.key)}
								className={`flex flex-col items-center gap-0.5 px-1.5 py-2 text-[10px] md:text-xs font-medium rounded-sm justify-center ${
									activeTab === tab.key
										? "bg-volt text-white"
										: "text-muted-foreground hover:text-foreground"
								}`}
							>
								<Icon className="w-4 h-4 shrink-0" />
								<span className="text-center leading-tight">
									{tab.shortLabel || tab.label}
								</span>
							</button>
						);
					})}
				</div>

				{/* ═══════ TAB: PROFILO ═══════ */}
				{activeTab === "profilo" && (
					<div className="space-y-4">
						<Card className="bg-card border-border rounded-sm">
							<CardContent className="p-4 space-y-3">
								<div className="flex items-start justify-between gap-3">
									<div className="min-w-0">
										<p className="label-tactical mb-1">
											{t("performancePublicLabel")}
										</p>
										<p className="text-xs text-muted-foreground">
											{t("performancePublicDescription")}
										</p>
									</div>
									<Switch
										checked={!!profileForm.performance_public}
										onCheckedChange={(value) =>
											setProfileForm({
												...profileForm,
												performance_public: !!value,
											})
										}
									/>
								</div>
								<div className="flex items-start justify-between gap-3 pt-3 border-t border-border">
									<div className="min-w-0">
										<p className="label-tactical mb-1">
											{t("resultsPublicLabel")}
										</p>
										<p className="text-xs text-muted-foreground">
											{t("resultsPublicDescription")}
										</p>
									</div>
									<Switch
										checked={profileForm.results_public !== false}
										onCheckedChange={(value) =>
											setProfileForm({
												...profileForm,
												results_public: !!value,
											})
										}
									/>
								</div>
							</CardContent>
						</Card>

						{/* Account info — read-only */}
						<Card className="bg-card border-border rounded-sm">
							<CardContent className="p-4 space-y-3">
								<p className="label-tactical mb-3">{t("accountLabel")}</p>
								<div className="flex items-center gap-3">
									<Mail className="w-4 h-4 text-muted-foreground shrink-0" />
									<div className="min-w-0">
										<p className="text-xs text-muted-foreground">
											{t("email")}
										</p>
										<p className="text-sm truncate">{user?.email}</p>
									</div>
								</div>
								<div className="flex items-center gap-3">
									<Clock className="w-4 h-4 text-muted-foreground shrink-0" />
									<div>
										<p className="text-xs text-muted-foreground">
											{t("memberSince")}
										</p>
										<p className="text-sm">
											{user?.created_at
												? new Date(user.created_at).toLocaleDateString(
														language === "it" ? "it-IT" : "en-US",
														{
															day: "numeric",
															month: "long",
															year: "numeric",
														},
													)
												: "—"}
										</p>
									</div>
								</div>
							</CardContent>
						</Card>

						{/* Editable fields */}
						<Card className="bg-card border-border rounded-sm overflow-hidden">
							<CardContent className="p-4 space-y-4">
								<p className="label-tactical mb-1">{t("personalData")}</p>

								<div className="space-y-1.5">
									<Label className="text-xs text-muted-foreground">
										{t("name")}
									</Label>
									<Input
										value={profileForm.name}
										onChange={(e) =>
											setProfileForm({
												...profileForm,
												name: e.target.value,
											})
										}
										className="bg-secondary border-transparent rounded-sm h-11"
									/>
								</div>

								<div className="space-y-1.5">
									<Label className="text-xs text-muted-foreground">
										{t("surname")}
									</Label>
									<Input
										value={profileForm.surname}
										onChange={(e) =>
											setProfileForm({
												...profileForm,
												surname: e.target.value,
											})
										}
										placeholder={t("yourSurname")}
										className="bg-secondary border-transparent rounded-sm h-11"
									/>
								</div>

								<div className="space-y-1.5">
									<Label className="text-xs text-muted-foreground">
										{t("nickname")}
									</Label>
									<Input
										value={profileForm.nickname}
										onChange={(e) =>
											setProfileForm({
												...profileForm,
												nickname: e.target.value,
											})
										}
										placeholder={t("nicknamePlaceholder")}
										className="bg-secondary border-transparent rounded-sm h-11"
									/>
								</div>

								<div className="space-y-1.5">
									<Label className="text-xs text-muted-foreground">
										{t("genderLabel")}
									</Label>
									<Select
										value={profileForm.gender || "__none__"}
										onValueChange={(value) =>
											setProfileForm({
												...profileForm,
												gender: value === "__none__" ? "" : value,
											})
										}
									>
										<SelectTrigger className="bg-secondary border-transparent rounded-sm h-11">
											<SelectValue placeholder={t("selectGender")} />
										</SelectTrigger>
										<SelectContent className="bg-card border-border">
											<SelectItem value="__none__">
												{t("noneOption")}
											</SelectItem>
											<SelectItem value="male">{t("genderMale")}</SelectItem>
											<SelectItem value="female">
												{t("genderFemale")}
											</SelectItem>
										</SelectContent>
									</Select>
								</div>

								<div className="space-y-1.5">
									<Label className="text-xs text-muted-foreground">
										{t("dateOfBirth")}
									</Label>
									<Input
										type="date"
										value={profileForm.date_of_birth}
										onChange={(e) =>
											setProfileForm({
												...profileForm,
												date_of_birth: e.target.value,
											})
										}
										className="bg-secondary border-transparent rounded-sm h-11 min-w-0 appearance-none"
									/>
								</div>

								<div className="grid grid-cols-2 gap-3">
									<div className="space-y-1.5">
										<Label className="text-xs text-muted-foreground flex items-center gap-1">
											<Weight className="w-3 h-3" /> {t("weightKg")}
										</Label>
										<Input
											type="number"
											step="0.1"
											value={profileForm.weight}
											onChange={(e) =>
												setProfileForm({
													...profileForm,
													weight: e.target.value,
												})
											}
											className="bg-secondary border-transparent rounded-sm h-11"
											placeholder="75"
										/>
									</div>
									<div className="space-y-1.5">
										<Label className="text-xs text-muted-foreground flex items-center gap-1">
											<Ruler className="w-3 h-3" /> {t("heightCm")}
										</Label>
										<Input
											type="number"
											value={profileForm.height}
											onChange={(e) =>
												setProfileForm({
													...profileForm,
													height: e.target.value,
												})
											}
											className="bg-secondary border-transparent rounded-sm h-11"
											placeholder="175"
										/>
									</div>
								</div>
							</CardContent>
						</Card>

						{/* Sport */}
						<Card className="bg-card border-border rounded-sm">
							<CardContent className="p-4 space-y-3">
								<p className="label-tactical mb-1">{t("sportLabel")}</p>
								<p className="text-xs text-muted-foreground">
									{t("sportsMultiHint")}
								</p>
								{/* Lo stesso selettore che usa il coach nella scheda atleta. */}
								<SportsPicker
									options={sportsOptions}
									value={profileForm.sports || []}
									onChange={(next) =>
										setProfileForm((prev) => ({ ...prev, sports: next }))
									}
								/>
								<SportsExplainer audience="athlete" />
							</CardContent>
						</Card>
					</div>
				)}

				{/* ═══════ TAB: ESERCIZI ═══════ */}
				{activeTab === "esercizi" && hasDomain(user, DOMAIN_PHYSICAL) && (
					<div className="space-y-4">
						{/* Category filter */}
						<div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 no-scrollbar">
							{availableCategories.map((cat) => (
								<button
									key={cat}
									onClick={() => setCategoryFilter(cat)}
									className={`shrink-0 px-3 py-1.5 text-[11px] font-medium rounded-sm border ${
										categoryFilter === cat
											? "bg-volt text-white border-volt"
											: "bg-secondary border-transparent text-muted-foreground hover:text-foreground"
									}`}
								>
									{cat === "all"
										? t("allCategories")
										: CATEGORY_LABELS[cat] || cat}
								</button>
							))}
						</div>

						{/* Search */}
						<div className="relative">
							<Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
							<Input
								value={exerciseSearch}
								onChange={(e) => setExerciseSearch(e.target.value)}
								placeholder={t("searchExercise")}
								className="pl-10 bg-secondary border-transparent rounded-sm h-11"
							/>
						</div>

						{enrichedExerciseStats.length === 0 ? (
							<Card className="bg-card border-border rounded-sm">
								<CardContent className="flex flex-col items-center justify-center py-12 text-center">
									<Dumbbell className="w-10 h-10 text-muted-foreground mb-3" />
									<p className="font-heading text-base mb-1">
										No exercises yet
									</p>
									<p className="text-xs text-muted-foreground">
										Your exercises will appear here after your first workouts
									</p>
								</CardContent>
							</Card>
						) : (
							Object.entries(exercisesByCategory)
								.sort(([a], [b]) => {
									const ia = CATEGORY_ORDER.indexOf(a);
									const ib = CATEGORY_ORDER.indexOf(b);
									return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
								})
								.map(([category, items]) => (
									<div key={category}>
										<div className="flex items-center gap-2 mb-2">
											<Badge
												className={`${CATEGORY_COLORS[category] || "bg-secondary text-foreground"} border-0 text-[10px]`}
											>
												{CATEGORY_LABELS[category] || category}
											</Badge>
											<span className="text-[10px] text-muted-foreground">
												{items.length} esercizi
											</span>
										</div>
										<div className="space-y-1.5">
											{items.map((ex) => {
												const isCond = ex.category === "conditioning";
												const cfg = ex.conditioning_config || null;
												const condType =
													cfg?.conditioning_type || ex.conditioning_type;
												const condExercises = cfg?.conditioning_exercises || [];
												const rounds = cfg?.rounds;
												const timeCap = cfg?.time_cap_seconds;
												const interval = cfg?.interval_seconds;
												const intervalLabel = formatIntervalSecondsLabel(
													interval,
													language,
												);
												const freeCustomText = String(
													cfg?.conditioning_free_text ||
														cfg?.custom_workout_text ||
														ex?.conditioning_free_text ||
														ex?.custom_workout_text ||
														"",
												).trim();
												return (
													<div key={ex.exercise_id}>
														<button
															onClick={() =>
																handleSelectExercise(ex.exercise_id)
															}
															className={`w-full text-left p-3 rounded-sm border ${
																selectedExerciseId === ex.exercise_id
																	? "border-volt/50 bg-volt/5"
																	: "border-border bg-card hover:border-volt/20"
															}`}
														>
															<div className="flex items-center gap-3">
																{/* Icon */}
																<div className="shrink-0">
																	{isCond ? (
																		<div className="w-7 h-7 rounded-full bg-yellow-500/20 flex items-center justify-center">
																			<Zap className="w-3.5 h-3.5 text-yellow-400" />
																		</div>
																	) : (
																		<div className="w-7 h-7 rounded-full bg-volt/20 flex items-center justify-center">
																			<Dumbbell className="w-3.5 h-3.5 text-volt" />
																		</div>
																	)}
																</div>
																<div className="flex-1 min-w-0">
																	{isCond ? (
																		<>
																			{/* Conditioning header — like homepage */}
																			<p className="text-sm font-medium">
																				{condType === "for_time" && (
																					<>
																						{rounds && (
																							<>
																								{rounds}{" "}
																								{rounds === 1
																									? "Round"
																									: "Rounds"}{" "}
																							</>
																						)}
																						For Time
																						{timeCap && (
																							<span className="text-muted-foreground font-normal text-xs">
																								{" "}
																								· TC {Math.floor(timeCap / 60)}'
																							</span>
																						)}
																					</>
																				)}
																				{condType === "amrap" && (
																					<>
																						{timeCap && (
																							<>{Math.floor(timeCap / 60)}' </>
																						)}
																						AMRAP
																					</>
																				)}
																				{condType === "emom" && (
																					<>
																						EMOM
																						{intervalLabel && (
																							<span className="text-muted-foreground font-normal text-xs">
																								{" "}
																								ogni {intervalLabel}
																							</span>
																						)}
																						{rounds && (
																							<span className="text-muted-foreground font-normal text-xs">
																								{" "}
																								· {rounds}{" "}
																								{rounds === 1
																									? "round"
																									: "rounds"}
																							</span>
																						)}
																					</>
																				)}
																				{(condType === "free" ||
																					condType === "custom") &&
																					(freeCustomText ||
																						t("customWodLabel"))}
																				{!condType && ex.exercise_name}
																			</p>
																			<div className="flex items-center gap-1 mt-0.5 text-[10px] text-volt">
																				<ExternalLink className="w-3 h-3" />
																				<span>Tocca per dettagli</span>
																			</div>
																			{/* Sub-exercises */}
																			{condExercises.length > 0 && (
																				<div className="space-y-0.5 mt-0.5">
																					{condExercises.map((ce, j) => (
																						<span
																							key={j}
																							className="block text-[10px] text-muted-foreground"
																						>
																							{ce.reps && (
																								<span className="font-medium text-foreground">
																									{ce.reps}{" "}
																								</span>
																							)}
																							{ce.exercise_name || ce.name}
																						</span>
																					))}
																				</div>
																			)}
																			<span className="text-[10px] text-muted-foreground mt-0.5 block">
																				{ex.count}{" "}
																				{ex.count === 1 ? "volta" : "volte"}
																			</span>
																		</>
																	) : (
																		<>
																			<div className="flex items-center gap-2">
																				<p className="text-sm font-medium truncate">
																					{ex.exercise_name}
																				</p>
																				{(ex.video_url || ex.image_url) && (
																					<div className="w-5 h-5 rounded-sm bg-volt/10 flex items-center justify-center shrink-0">
																						{ex.video_url ? (
																							<Play className="w-3 h-3 text-volt" />
																						) : (
																							<Image className="w-3 h-3 text-volt" />
																						)}
																					</div>
																				)}
																			</div>
																			<div className="flex items-center gap-3 mt-0.5">
																				<span className="text-[10px] text-muted-foreground">
																					{ex.count}{" "}
																					{ex.count === 1 ? "volta" : "volte"}
																				</span>
																				{ex.estimated_1rm && (
																					<span className="text-[10px] text-volt font-medium">
																						1RM: {ex.estimated_1rm}kg
																					</span>
																				)}
																				{ex.max_weight && !ex.estimated_1rm && (
																					<span className="text-[10px] text-muted-foreground">
																						Max: {ex.max_weight}kg
																					</span>
																				)}
																			</div>
																		</>
																	)}
																</div>
																<ChevronDown
																	className={`w-4 h-4 text-muted-foreground shrink-0 ${
																		selectedExerciseId === ex.exercise_id
																			? "rotate-180"
																			: ""
																	}`}
																/>
															</div>
														</button>

														{/* Expanded detail */}
														{selectedExerciseId === ex.exercise_id && (
															<div className="mt-1.5 p-3 bg-card border border-volt/20 rounded-sm space-y-3">
																{historyLoading ? (
																	<div className="flex justify-center py-4">
																		<div className="spinner"></div>
																	</div>
																) : (
																	<>
																		{/* Conditioning: open detail modal */}
																		{isCond && (
																			<button
																				onClick={() =>
																					setConditioningPreviewExercise(ex)
																				}
																				className="w-full flex items-center justify-center gap-2 py-2 px-3 bg-secondary/50 border border-border rounded-sm text-xs text-muted-foreground hover:text-foreground hover:border-volt/30 active:opacity-70"
																			>
																				<ExternalLink className="w-3.5 h-3.5" />
																				Dettagli esercizio
																			</button>
																		)}

																		{/* Media preview — strength only */}
																		{!isCond &&
																			(ex.video_url || ex.image_url) && (
																				<button
																					onClick={() => setMediaExercise(ex)}
																					className="w-full relative rounded-sm overflow-hidden bg-secondary/30 h-36 flex items-center justify-center group active:opacity-80"
																				>
																					{ex.image_url ? (
																						<img
																							src={getImageUrl(ex.image_url)}
																							alt={ex.exercise_name}
																							className="w-full h-full object-cover"
																						/>
																					) : getYouTubeThumb(ex.video_url) ? (
																						<img
																							src={getYouTubeThumb(
																								ex.video_url,
																							)}
																							alt={ex.exercise_name}
																							className="w-full h-full object-cover"
																						/>
																					) : (
																						<Play className="w-8 h-8 text-muted-foreground" />
																					)}
																					<div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent flex items-center justify-center">
																						<span className="text-xs font-medium text-white flex items-center gap-1.5">
																							{ex.video_url ? (
																								<>
																									<Play className="w-4 h-4" />{" "}
																									Guarda Video
																								</>
																							) : (
																								<>
																									<Image className="w-4 h-4" />{" "}
																									Vedi Guida
																								</>
																							)}
																						</span>
																					</div>
																				</button>
																			)}

																		{/* Description — strength only */}
																		{!isCond && ex.description && (
																			<p className="text-xs text-muted-foreground italic">
																				{ex.description}
																			</p>
																		)}

																		{/* PR card — strength only */}
																		{ex.category !== "conditioning" &&
																			ex.estimated_1rm && (
																				<div className="flex items-center gap-3 p-3 bg-volt/10 rounded-sm">
																					<Award className="w-5 h-5 text-volt shrink-0" />
																					<div>
																						<p className="text-[10px] text-muted-foreground uppercase tracking-wider">
																							Record Personale
																						</p>
																						<p className="font-heading text-lg text-volt">
																							{ex.pr_weight}kg x {ex.pr_reps}{" "}
																							rep
																							<span className="text-sm text-muted-foreground ml-2">
																								(1RM: {ex.estimated_1rm}
																								kg)
																							</span>
																						</p>
																					</div>
																				</div>
																			)}

																		{/* Conditioning best scores */}
																		{ex.category === "conditioning" &&
																			exerciseHistory.length > 0 &&
																			(() => {
																				const bestScore =
																					exerciseHistory.reduce(
																						(best, r) =>
																							getNumericConditioningValue(r) !==
																								null &&
																							(!best ||
																								getNumericConditioningValue(r) >
																									best)
																								? getNumericConditioningValue(r)
																								: best,
																						null,
																					);
																				const bestTime = exerciseHistory.reduce(
																					(best, r) =>
																						r.time_seconds &&
																						(!best || r.time_seconds < best)
																							? r.time_seconds
																							: best,
																					null,
																				);
																				return bestScore || bestTime ? (
																					<div className="flex items-center gap-3 p-3 bg-orange-500/10 rounded-sm">
																						<Award className="w-5 h-5 text-orange-500 shrink-0" />
																						<div>
																							<p className="text-[10px] text-muted-foreground uppercase tracking-wider">
																								Miglior Score
																							</p>
																							<p className="font-heading text-lg text-orange-500">
																								{bestScore && (
																									<span>{bestScore} reps</span>
																								)}
																								{bestScore && bestTime && (
																									<span className="text-muted-foreground mx-1">
																										·
																									</span>
																								)}
																								{bestTime && (
																									<span>
																										{Math.floor(bestTime / 60)}:
																										{(bestTime % 60)
																											.toString()
																											.padStart(2, "0")}
																									</span>
																								)}
																							</p>
																						</div>
																					</div>
																				) : null;
																			})()}

																		{/* Mini chart — strength: weight, conditioning: score */}
																		{ex.category === "conditioning"
																			? (() => {
																					const scoreData = exerciseHistory
																						.map((r) => ({
																							date: r.date,
																							score:
																								getNumericConditioningValue(r),
																						}))
																						.filter((r) => r.score !== null)
																						.slice(0, 20)
																						.reverse()
																						.map((r) => ({
																							date: r.date?.substring(5, 10),
																							score: r.score,
																						}));
																					return scoreData.length > 1 ? (
																						<div className="h-[140px]">
																							<ResponsiveContainer
																								width="100%"
																								height="100%"
																							>
																								<LineChart data={scoreData}>
																									<CartesianGrid
																										strokeDasharray="3 3"
																										stroke={chartColors.grid}
																									/>
																									<XAxis
																										dataKey="date"
																										stroke={chartColors.axis}
																										tick={{
																											fill: chartColors.axis,
																											fontSize: 10,
																										}}
																									/>
																									<YAxis
																										stroke={chartColors.axis}
																										tick={{
																											fill: chartColors.axis,
																											fontSize: 10,
																										}}
																										width={35}
																									/>
																									<Tooltip
																										contentStyle={tooltipStyle}
																										formatter={(v) => [
																											`${v} reps`,
																											"Score",
																										]}
																									/>
																									<Line
																										type="monotone"
																										dataKey="score"
																										stroke={chartColors.amber}
																										strokeWidth={2}
																										dot={{
																											fill: chartColors.amber,
																											r: 3,
																										}}
																									/>
																								</LineChart>
																							</ResponsiveContainer>
																						</div>
																					) : null;
																				})()
																			: chartData.length > 1 && (
																					<div className="space-y-2">
																						<div className="flex flex-wrap items-center gap-3 text-[10px] text-muted-foreground">
																							<div className="flex items-center gap-1.5">
																								<span className="w-2.5 h-2.5 rounded-full bg-volt" />
																								<span>Peso</span>
																							</div>
																							<div className="flex items-center gap-1.5">
																								<span className="w-2.5 h-2.5 rounded-full bg-acid" />
																								<span>1RM stimato</span>
																							</div>
																						</div>
																						<div className="h-[140px]">
																							<ResponsiveContainer
																								width="100%"
																								height="100%"
																							>
																								<LineChart data={chartData}>
																									<CartesianGrid
																										strokeDasharray="3 3"
																										stroke={chartColors.grid}
																									/>
																									<XAxis
																										dataKey="date"
																										stroke={chartColors.axis}
																										tick={{
																											fill: chartColors.axis,
																											fontSize: 10,
																										}}
																									/>
																									<YAxis
																										stroke={chartColors.axis}
																										tick={{
																											fill: chartColors.axis,
																											fontSize: 10,
																										}}
																										domain={[
																											"dataMin - 5",
																											"dataMax + 5",
																										]}
																										width={35}
																									/>
																									<Tooltip
																										contentStyle={tooltipStyle}
																										formatter={(v, name) => {
																											const label =
																												name === "estimated1rm"
																													? "1RM stimato"
																													: "Peso";
																											return [`${v} kg`, label];
																										}}
																									/>
																									<Line
																										type="monotone"
																										dataKey="weight"
																										name="weight"
																										stroke={chartColors.blue}
																										strokeWidth={2}
																										dot={{
																											fill: chartColors.blue,
																											r: 3,
																										}}
																									/>
																									<Line
																										type="monotone"
																										dataKey="estimated1rm"
																										name="estimated1rm"
																										stroke={chartColors.acid}
																										strokeWidth={2}
																										dot={{
																											fill: chartColors.acid,
																											r: 3,
																										}}
																									/>
																								</LineChart>
																							</ResponsiveContainer>
																						</div>
																					</div>
																				)}

																		{/* History list */}
																		<ScrollArea className="max-h-[200px]">
																			<div className="space-y-1">
																				{exerciseHistory
																					.slice(0, 15)
																					.map((r, i) => (
																						<div
																							key={i}
																							className="py-1.5 px-2 rounded-sm hover:bg-white/5 text-sm"
																						>
																							<div className="flex items-center justify-between gap-3">
																								<span className="text-muted-foreground text-xs">
																									{r.date}
																								</span>
																								<span className="text-right text-xs font-mono text-volt">
																									{formatHistorySummary(r)}
																								</span>
																							</div>
																							{r.notes && (
																								<p className="text-[10px] text-muted-foreground mt-0.5">
																									{r.notes}
																								</p>
																							)}
																						</div>
																					))}
																			</div>
																		</ScrollArea>
																	</>
																)}
															</div>
														)}
													</div>
												);
											})}
										</div>
									</div>
								))
						)}
					</div>
				)}

				{/* ═══════ TAB: ASSEGNAZIONI ═══════ */}
				{activeTab === "assegnazioni" && (
					<div className="space-y-4">
						<Card className="bg-card border-border rounded-sm">
							<CardContent className="p-4 space-y-3">
								<p className="label-tactical">{t("trainingGroupsTitle")}</p>
								{assignments.groups.length > 0 ? (
									<div className="space-y-2.5">
										{assignments.groups.map((group) => (
											<div
												key={group.group_id}
												className="p-3 bg-secondary rounded-sm"
											>
												<div className="flex items-start justify-between gap-3">
													<div className="min-w-0">
														<p className="font-heading text-sm truncate">
															{group.name || "-"}
														</p>
														{group.description ? (
															<p className="text-xs text-muted-foreground mt-1 line-clamp-2">
																{group.description}
															</p>
														) : null}
													</div>
													<Button
														variant="outline"
														size="sm"
														onClick={() => setSelectedAssignmentGroup(group)}
														className="border-dashed border-volt/40 text-volt rounded-sm h-9 px-3 shrink-0"
													>
														{t("viewGroupDetails")}
													</Button>
												</div>
												<div className="flex items-center gap-2 mt-2">
													<Badge className="bg-volt/20 text-volt border-0 rounded-sm text-[10px]">
														{(group.active_programs || []).length}{" "}
														{t("activeProgramsCountLabel")}
													</Badge>
													<Badge className="bg-acid/20 text-acid border-0 rounded-sm text-[10px]">
														{(group.teammates || []).length}{" "}
														{t("teammatesCountLabel")}
													</Badge>
												</div>
											</div>
										))}
									</div>
								) : (
									<div className="text-center py-4 text-sm text-muted-foreground">
										{t("noTrainingGroupsAssigned")}
									</div>
								)}
							</CardContent>
						</Card>

						<Dialog
							open={!!selectedAssignmentGroup}
							onOpenChange={(open) => {
								if (!open) setSelectedAssignmentGroup(null);
							}}
						>
							<DialogContent className="bg-card border-border max-w-lg w-[calc(100vw-2rem)] max-h-[82vh] overflow-y-auto">
								<DialogDescription className="hidden" />
								<DialogHeader>
									{/* Il bottone "Esci dal gruppo" era qui in alto a destra e
									    finiva sotto la X di chiusura della modale: su mobile si
									    rischiava di lasciare il gruppo per sbaglio. Spostato in
									    fondo (vedi footer), lontano dalla X. */}
									<DialogTitle className="font-heading truncate pr-8">
										{selectedAssignmentGroup?.name || t("groupDetailsTitle")}
									</DialogTitle>
								</DialogHeader>

								{selectedAssignmentGroup && (
									<div className="space-y-4">
										{selectedAssignmentGroup.description ? (
											<p className="text-sm text-muted-foreground">
												{selectedAssignmentGroup.description}
											</p>
										) : null}

										<div className="space-y-2">
											<p className="label-tactical">
												{t("activeProgramsTitle")}
											</p>
											{(selectedAssignmentGroup.active_programs || []).length >
											0 ? (
												<div className="max-h-52 overflow-y-auto pr-2">
													<div className="space-y-2">
														{selectedAssignmentGroup.active_programs.map(
															(program) => (
																<div
																	key={program.program_id}
																	className="p-2.5 bg-secondary rounded-sm text-sm"
																>
																	{program.title}
																</div>
															),
														)}
													</div>
												</div>
											) : (
												<p className="text-sm text-muted-foreground">
													{t("noActiveProgramsInGroup")}
												</p>
											)}
										</div>

										<div className="space-y-2">
											<p className="label-tactical">{t("teammatesTitle")}</p>
											{(selectedAssignmentGroup.teammates || []).length > 0 ? (
												<div className="max-h-52 overflow-y-auto pr-2">
													<div className="space-y-2">
														{selectedAssignmentGroup.teammates.map(
															(teammate) => (
																<div
																	key={teammate.user_id}
																	className="flex items-center gap-2.5 p-2.5 bg-secondary rounded-sm"
																>
																	<div className="w-8 h-8 rounded-sm bg-volt/20 overflow-hidden shrink-0 flex items-center justify-center">
																		{teammate.picture ? (
																			<img
																				src={getImageUrl(teammate.picture)}
																				alt={teammate.name || "Teammate"}
																				className="w-full h-full object-cover"
																			/>
																		) : (
																			<span className="font-heading text-[10px] text-volt">
																				{getPersonInitials(teammate.name)}
																			</span>
																		)}
																	</div>
																	<p className="text-sm truncate">
																		{teammate.name}
																	</p>
																</div>
															),
														)}
													</div>
												</div>
											) : (
												<p className="text-sm text-muted-foreground">
													{t("noTeammatesInGroup")}
												</p>
											)}
										</div>

										{selectedAssignmentGroup.group_id && (
											<div className="pt-2 border-t border-border">
												<Button
													type="button"
													variant="outline"
													onClick={() =>
														handleLeaveTrainingGroup(selectedAssignmentGroup)
													}
													className="w-full h-11 rounded-sm border-dashed border-blaze/50 text-blaze hover:bg-blaze/10"
												>
													{t("leaveTrainingGroupAction")}
												</Button>
											</div>
										)}
									</div>
								)}
							</DialogContent>
						</Dialog>
					</div>
				)}

				{/* ═══════ TAB: CALENDARIO ═══════ */}
				{activeTab === "tiro" && hasDomain(user, DOMAIN_SHOOTING) && (
					<ShootingProfileSection
						athleteId={user?.user_id}
						shootingSports={shootingSportsOptions}
						shootingClasses={profileForm.shooting_classes || {}}
						onClassesChange={(next) =>
							setProfileForm((prev) => ({ ...prev, shooting_classes: next }))
						}
					/>
				)}

				{activeTab === "calendario" && (
					<div className="space-y-4">
						<Button
							onClick={openNewEventDialog}
							className="w-full bg-volt hover:bg-volt/90 rounded-sm h-11"
						>
							<Plus className="w-4 h-4 mr-2" />
							{t("addEvent")}
						</Button>

						{/* Mesocicli configurati dal coach: sola lettura */}
						{mesocycleCards.length > 0 && (
							<div>
								<p className="label-tactical mb-2">{t("preparationPlan")}</p>
								<div className="space-y-2">
									{mesocycleCards.map((meso) => {
										const statusMeta =
											meso.status === "current"
												? {
														label: t("mesocycleInProgress"),
														className: "bg-volt/20 text-volt",
													}
												: meso.status === "upcoming"
													? {
															label: t("mesocycleUpcoming"),
															className: "bg-yellow-500/20 text-yellow-500",
														}
													: {
															label: t("mesocycleCompleted"),
															className: "bg-muted text-muted-foreground",
														};
										return (
											<Card
												key={meso.mesocycle_id}
												className={`bg-card border-border rounded-sm overflow-hidden ${
													meso.status === "completed" ? "opacity-60" : ""
												}`}
											>
												<div
													className="h-1 w-full"
													style={{ backgroundColor: meso.color || chartColors.blue }}
												/>
												<CardContent className="p-4">
													<div className="flex items-start justify-between gap-2">
														<div className="flex items-center gap-2 min-w-0">
															<span
																className="w-2.5 h-2.5 rounded-full shrink-0"
																style={{
																	backgroundColor: meso.color || chartColors.blue,
																}}
															/>
															<p className="font-heading text-sm truncate">
																{meso.name}
															</p>
															{meso.source === "group" && (
																<Badge className="bg-secondary text-muted-foreground border-0 text-[10px] shrink-0">
																	{t("pbSeasonGroupBadge")}
																	{meso.group_name ? ` · ${meso.group_name}` : ""}
																</Badge>
															)}
														</div>
														<Badge
															className={`border-0 text-[10px] shrink-0 ${statusMeta.className}`}
														>
															{statusMeta.label}
														</Badge>
													</div>
													<div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mt-2">
														<span className="flex items-center gap-1">
															<Calendar className="w-3 h-3" />
															{new Date(
																`${meso.start_date}T00:00:00`,
															).toLocaleDateString(
																language === "it" ? "it-IT" : "en-US",
																{ day: "numeric", month: "short" },
															)}
															{" – "}
															{new Date(
																`${meso.end_date}T00:00:00`,
															).toLocaleDateString(
																language === "it" ? "it-IT" : "en-US",
																{
																	day: "numeric",
																	month: "short",
																	year: "numeric",
																},
															)}
														</span>
														<span className="flex items-center gap-1">
															<Clock className="w-3 h-3" />
															{meso.weeks}{" "}
															{meso.weeks === 1
																? t("weekSingular")
																: t("weeksPlural")}
														</span>
													</div>
													{meso.status === "upcoming" &&
														meso.daysToStart > 0 && (
															<p className="text-[11px] text-muted-foreground mt-1">
																{t("mesocycleStartsIn")} {meso.daysToStart}{" "}
																{t("days")}
															</p>
														)}
													{meso.status === "current" && (
														<div className="mt-3">
															<div className="h-1.5 w-full bg-muted rounded-sm overflow-hidden">
																<div
																	className="h-full rounded-sm transition-[width]"
																	style={{
																		width: `${meso.progress}%`,
																		backgroundColor: meso.color || chartColors.blue,
																	}}
																/>
															</div>
															<p className="text-[11px] text-muted-foreground mt-1">
																{meso.progress}%
															</p>
														</div>
													)}
													{meso.description && (
														<p className="text-xs text-muted-foreground mt-2 whitespace-pre-line">
															{meso.description}
														</p>
													)}
												</CardContent>
											</Card>
										);
									})}
								</div>
							</div>
						)}

						{/* Upcoming */}
						<div>
							<p className="label-tactical mb-2">{t("upcomingEvents")}</p>
							{upcomingEvents.length > 0 ? (
								<div className="space-y-2">
									{upcomingEvents.map((event) => {
										const daysUntil = Math.ceil(
											(new Date(event.date) - new Date(today)) /
												(1000 * 60 * 60 * 24),
										);
										return (
											<Card
												key={event.event_id}
												className="bg-card border-border rounded-sm"
											>
												<CardContent className="p-4">
													<div className="flex items-start justify-between">
														<div className="flex-1 min-w-0">
															<div className="flex items-center gap-2 mb-1 flex-wrap">
																<p className="font-heading text-sm truncate">
																	{event.name}
																</p>
																{event.event_type && (
																	<Badge className="bg-volt/20 text-volt border-0 text-[10px]">
																		{EVENT_TYPES.find(
																			(t) => t.value === event.event_type,
																		)?.label || event.event_type}
																	</Badge>
																)}
																{(event.shared_athlete_count || 1) >= 2 && (
																	<SharedAthletesBadge
																		count={event.shared_athlete_count}
																		onClick={() =>
																			openEventParticipants(event)
																		}
																	/>
																)}
															</div>
															<div className="flex items-center gap-3 text-xs text-muted-foreground">
																<span className="flex items-center gap-1">
																	<Calendar className="w-3 h-3" />
																	{new Date(event.date).toLocaleDateString(
																		language === "it" ? "it-IT" : "en-US",
																		{
																			day: "numeric",
																			month: "short",
																			year: "numeric",
																		},
																	)}
																</span>
																{event.location && (
																	<span className="flex items-center gap-1">
																		<MapPin className="w-3 h-3" />
																		{event.location}
																	</span>
																)}
															</div>
															{event.notes && (
																<p className="text-xs text-muted-foreground mt-1">
																	{event.notes}
																</p>
															)}
														</div>
														<div className="flex items-center gap-2 shrink-0 ml-3">
															<div className="text-right">
																<p className="font-heading text-lg text-volt">
																	{daysUntil}
																</p>
																<p className="text-[10px] text-muted-foreground">
																	{t("days")}
																</p>
															</div>
															<div className="flex flex-col gap-1">
																<Button
																	variant="ghost"
																	size="icon"
																	className="h-8 w-8 text-muted-foreground hover:text-volt rounded-sm"
																	onClick={() => openEditEvent(event)}
																>
																	<Edit2 className="w-3.5 h-3.5" />
																</Button>
																<Button
																	variant="ghost"
																	size="icon"
																	className="h-8 w-8 text-muted-foreground hover:text-blaze rounded-sm"
																	onClick={() =>
																		handleDeleteEvent(event.event_id)
																	}
																>
																	<Trash2 className="w-3.5 h-3.5" />
																</Button>
															</div>
														</div>
													</div>
												</CardContent>
											</Card>
										);
									})}
								</div>
							) : (
								<Card className="bg-card border-border rounded-sm">
									<CardContent className="py-8 text-center">
										<Calendar className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
										<p className="text-sm text-muted-foreground">
											{t("noEventsScheduled")}
										</p>
									</CardContent>
								</Card>
							)}
						</div>

						{/* Races of the other athletes of my coaches.
						    Deliberately wider than the rest of the calendar: other
						    sports and other training groups included, read-only. */}
						<div>
							<button
								type="button"
								onClick={toggleOtherEvents}
								className="w-full flex items-center justify-between gap-2 p-3 bg-card border border-border rounded-sm text-left"
							>
								<div className="min-w-0">
									<p className="text-sm">{t("competitionOthersTitle")}</p>
									<p className="text-[10px] text-muted-foreground">
										{t("competitionOthersHint")}
									</p>
								</div>
								{otherEventsOpen ? (
									<ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
								) : (
									<ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
								)}
							</button>

							{otherEventsOpen && (
								<div className="mt-2 space-y-1.5">
									{otherEventsLoading ? (
										<p className="text-xs text-muted-foreground px-3 py-4 text-center">
											{t("loading")}
										</p>
									) : otherEvents.length > 0 ? (
										otherEvents.map((row) => (
											<div
												key={row.group_key}
												className="flex items-center justify-between gap-2 p-3 bg-card border border-border rounded-sm"
											>
												<div className="min-w-0">
													<div className="flex items-center gap-2">
														<p className="text-sm truncate">{row.name}</p>
														<SharedAthletesBadge
															count={row.athlete_count}
															onClick={() => openSuggestionParticipants(row)}
														/>
													</div>
													<p className="text-[10px] text-muted-foreground">
														{new Date(row.date).toLocaleDateString(
															language === "it" ? "it-IT" : "en-US",
															{
																day: "numeric",
																month: "short",
																year: "numeric",
															},
														)}
														{row.location ? ` · ${row.location}` : ""}
														{row.discipline ? ` · ${row.discipline}` : ""}
													</p>
												</div>
												<Button
													variant="ghost"
													size="icon"
													className="h-8 w-8 text-muted-foreground hover:text-volt rounded-sm shrink-0"
													title={t("competitionOthersAdd")}
													aria-label={t("competitionOthersAdd")}
													onClick={() => addOtherEventToCalendar(row)}
												>
													<Plus className="w-3.5 h-3.5" />
												</Button>
											</div>
										))
									) : (
										<p className="text-xs text-muted-foreground px-3 py-4 text-center">
											{t("competitionOthersEmpty")}
										</p>
									)}
								</div>
							)}
						</div>

						{/* Past events */}
						{pastEvents.length > 0 && (
							<div>
								<p className="label-tactical mb-2">{t("pastEvents")}</p>
								<div className="space-y-1.5">
									{pastEvents.map((event) => (
										<div
											key={event.event_id}
											className="flex items-center justify-between p-3 bg-card border border-border rounded-sm opacity-60"
										>
											<div className="min-w-0">
												<div className="flex items-center gap-2">
													<p className="text-sm truncate">{event.name}</p>
													{(event.shared_athlete_count || 1) >= 2 && (
														<SharedAthletesBadge
															count={event.shared_athlete_count}
															onClick={() => openEventParticipants(event)}
														/>
													)}
												</div>
												<p className="text-[10px] text-muted-foreground">
													{new Date(event.date).toLocaleDateString(
														language === "it" ? "it-IT" : "en-US",
														{
															day: "numeric",
															month: "short",
															year: "numeric",
														},
													)}
													{event.location ? ` · ${event.location}` : ""}
												</p>
											</div>
											<div className="flex items-center gap-1 shrink-0">
												<Button
													variant="ghost"
													size="icon"
													className="h-8 w-8 text-muted-foreground hover:text-volt rounded-sm"
													onClick={() => openEditEvent(event)}
												>
													<Edit2 className="w-3.5 h-3.5" />
												</Button>
												<Button
													variant="ghost"
													size="icon"
													className="h-8 w-8 text-muted-foreground hover:text-blaze rounded-sm shrink-0"
													onClick={() => handleDeleteEvent(event.event_id)}
												>
													<Trash2 className="w-3.5 h-3.5" />
												</Button>
											</div>
										</div>
									))}
								</div>
							</div>
						)}

						<CompetitionParticipantsDialog
							open={Boolean(participantsEvent)}
							onOpenChange={(open) => {
								if (!open) setParticipantsEvent(null);
							}}
							event={participantsEvent}
							athletes={participants}
							loading={participantsLoading}
						/>

						{/* Add/Edit event dialog */}
						<Dialog
							open={isEventDialogOpen}
							onOpenChange={(open) => {
								setIsEventDialogOpen(open);
								if (!open) {
									setEditingEvent(null);
									setAppliedSuggestionKey(null);
									setNewEvent({
										name: "",
										date: "",
										location: "",
										event_type: "gara",
										notes: "",
									});
								}
							}}
						>
							<DialogContent className="bg-card border-border max-w-sm w-[calc(100vw-2rem)] max-h-[calc(100dvh-2rem)] overflow-y-auto">
								<DialogDescription className="hidden" />
								<DialogHeader>
									<DialogTitle className="font-heading">
										{editingEvent ? t("editEvent") : t("newEvent")}
									</DialogTitle>
								</DialogHeader>
								<div className="space-y-3">
									<div className="space-y-1.5">
										<Label className="text-xs text-muted-foreground">
											{t("name")} *
										</Label>
										<Input
											value={newEvent.name}
											onChange={(e) => {
												setAppliedSuggestionKey(null);
												setNewEvent({
													...newEvent,
													name: e.target.value,
												});
											}}
											className="bg-secondary border-transparent rounded-sm h-11"
											placeholder={t("eventNamePlaceholder")}
										/>
										{visibleEventSuggestions.length > 0 && (
											<div className="rounded-sm border border-border bg-background p-2 space-y-1">
												<p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground px-1">
													{t("competitionSuggestionsTitle")}
												</p>
												<p className="text-[10px] text-muted-foreground px-1">
													{t("competitionSuggestionsHint")}
												</p>
												<div className="max-h-36 overflow-y-auto space-y-1">
													{visibleEventSuggestions.map((suggestion) => (
														<div
															key={suggestion.group_key}
															className="w-full min-h-11 px-2 py-2 rounded-sm hover:bg-secondary flex items-center gap-2"
														>
															<button
																type="button"
																onClick={() =>
																	applyEventSuggestion(suggestion)
																}
																className="min-w-0 flex-1 text-left"
															>
																<p className="text-sm truncate">
																	{suggestion.name}
																</p>
																<p className="text-xs text-muted-foreground truncate">
																	{new Date(suggestion.date).toLocaleDateString(
																		language === "it" ? "it-IT" : "en-US",
																		{ day: "numeric", month: "short" },
																	)}
																	{suggestion.location
																		? ` · ${suggestion.location}`
																		: ""}
																</p>
															</button>
															<SharedAthletesBadge
																count={suggestion.athlete_count}
																onClick={() =>
																	openSuggestionParticipants(suggestion)
																}
															/>
														</div>
													))}
												</div>
											</div>
										)}
									</div>
									<div className="space-y-1.5">
										<Label className="text-xs text-muted-foreground">
											{t("date")} *
										</Label>
										<Input
											type="date"
											value={newEvent.date}
											onChange={(e) =>
												setNewEvent({
													...newEvent,
													date: e.target.value,
												})
											}
											className="bg-secondary border-transparent rounded-sm h-11 w-full min-w-0 box-border [&::-webkit-calendar-picker-indicator]:opacity-50 [&::-webkit-date-and-time-value]:text-left"
										/>
									</div>
									<div className="space-y-1.5">
										<Label className="text-xs text-muted-foreground">
											{t("type")}
										</Label>
										<Select
											value={newEvent.event_type}
											onValueChange={(v) =>
												setNewEvent({
													...newEvent,
													event_type: v,
												})
											}
										>
											<SelectTrigger className="bg-secondary border-transparent rounded-sm h-11">
												<SelectValue />
											</SelectTrigger>
											<SelectContent className="bg-card border-border">
												{EVENT_TYPES.map((t) => (
													<SelectItem key={t.value} value={t.value}>
														{t.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
									<div className="space-y-1.5">
										<Label className="text-xs text-muted-foreground">
											{t("locationLabel")}
										</Label>
										<Input
											value={newEvent.location}
											onChange={(e) =>
												setNewEvent({
													...newEvent,
													location: e.target.value,
												})
											}
											className="bg-secondary border-transparent rounded-sm h-11"
											placeholder="Roma, ITA"
										/>
									</div>
									<div className="space-y-1.5">
										<Label className="text-xs text-muted-foreground">
											{t("notes")}
										</Label>
										<Input
											value={newEvent.notes}
											onChange={(e) =>
												setNewEvent({
													...newEvent,
													notes: e.target.value,
												})
											}
											className="bg-secondary border-transparent rounded-sm h-11"
											placeholder={t("eventNotesPlaceholder")}
										/>
									</div>
									<Button
										onClick={
											editingEvent ? handleUpdateEvent : handleCreateEvent
										}
										disabled={!newEvent.name || !newEvent.date}
										className="w-full bg-volt hover:bg-volt/90 rounded-sm h-11"
									>
										{editingEvent ? t("saveChanges") : t("addEvent")}
									</Button>
								</div>
							</DialogContent>
						</Dialog>
					</div>
				)}

				{/* ═══════ TAB: NOTIFICHE ═══════ */}
				{activeTab === "notifiche" && (
					<div>
						<PushNotificationSettingsCard
							notifPrefs={notifPrefs}
							pushSupported={pushSupported}
							pushServerConfigured={pushServerConfigured}
							pushBusy={pushBusy}
							savingNotifs={savingNotifs}
							onPushEnabledChange={handlePushEnabledChange}
							onNotifChange={handleNotifChange}
							showTestButton
							onSendPushTest={handleSendPushTest}
						/>
					</div>
				)}

				{activeTab === "salute" && user?.role === "athlete" && (
					<div className="space-y-4">
						<WellnessGovernanceCard />
					</div>
				)}
			</div>

			{/* Media Dialog */}
			<Dialog
				open={!!mediaExercise}
				onOpenChange={() => setMediaExercise(null)}
			>
				<DialogContent className="bg-card border-border max-w-lg">
					<DialogDescription className="hidden" />
					<DialogHeader>
						<DialogTitle className="font-heading">
							{mediaExercise?.exercise_name}
						</DialogTitle>
					</DialogHeader>
					{mediaExercise?.video_url &&
					getYouTubeEmbedUrl(mediaExercise.video_url) ? (
						<div className="aspect-video rounded-sm overflow-hidden">
							<iframe
								src={getYouTubeEmbedUrl(mediaExercise.video_url)}
								title={mediaExercise.exercise_name}
								className="w-full h-full"
								allowFullScreen
							/>
						</div>
					) : mediaExercise?.video_url ? (
						<a
							href={mediaExercise.video_url}
							target="_blank"
							rel="noopener noreferrer"
							className="flex items-center gap-2 text-volt hover:underline text-sm p-3 bg-secondary/50 rounded-sm"
						>
							<ExternalLink className="w-4 h-4" />
							Apri video esterno
						</a>
					) : mediaExercise?.image_url ? (
						<img
							src={getImageUrl(mediaExercise.image_url)}
							alt={mediaExercise.exercise_name}
							className="w-full rounded-sm"
						/>
					) : null}
					{mediaExercise?.description && (
						<p className="text-sm text-muted-foreground">
							{mediaExercise.description}
						</p>
					)}
				</DialogContent>
			</Dialog>

			{/* Conditioning Detail Dialog */}
			<Dialog
				open={!!conditioningPreviewExercise}
				onOpenChange={(open) => {
					if (!open) setConditioningPreviewExercise(null);
				}}
			>
				<DialogContent className="bg-card border-border max-w-lg max-h-[85vh] overflow-y-auto">
					<DialogDescription className="hidden" />
					<DialogHeader>
						<DialogTitle className="font-heading flex items-center gap-2">
							<Zap className="w-4 h-4 text-yellow-400" />
							{conditioningPreviewExercise?.exercise_name ||
								conditioningPreviewExercise?.name ||
								t("conditioning")}
						</DialogTitle>
					</DialogHeader>

					{(() => {
						const cfg =
							conditioningPreviewExercise?.conditioning_config ||
							conditioningPreviewExercise ||
							null;
						const condType =
							cfg?.conditioning_type ||
							conditioningPreviewExercise?.conditioning_type ||
							null;
						const condExercises = cfg?.conditioning_exercises || [];
						const emomRounds = getDisplayEmomRounds(cfg);
						const intervalLabel = formatIntervalSecondsLabel(
							cfg?.interval_seconds,
							language,
						);

						return (
							<div className="space-y-3">
								<div className="flex flex-wrap gap-2">
									{condType && (
										<Badge className="bg-yellow-500/20 text-yellow-400 border-0 rounded-sm text-[10px]">
											{String(condType).toUpperCase()}
										</Badge>
									)}
									{cfg?.time_cap_seconds && (
										<Badge className="bg-secondary text-muted-foreground border-0 rounded-sm text-[10px]">
											TC {Math.floor(cfg.time_cap_seconds / 60)}'
										</Badge>
									)}
									{cfg?.rounds && (
										<Badge className="bg-secondary text-muted-foreground border-0 rounded-sm text-[10px]">
											{cfg.rounds} {cfg.rounds === 1 ? "round" : "rounds"}
										</Badge>
									)}
									{intervalLabel && (
										<Badge className="bg-secondary text-muted-foreground border-0 rounded-sm text-[10px]">
											{t("everyLabel")} {intervalLabel}
										</Badge>
									)}
								</div>

								{condType === "emom" && emomRounds.length > 0 && (
									<div className="space-y-1.5">
										{emomRounds.map((round, j) => (
											<div
												key={round.round_id || j}
												className="border border-yellow-500/20 bg-yellow-500/10 rounded-sm px-2 py-1.5"
											>
												<div className="text-[10px] uppercase tracking-wider text-yellow-400 font-medium mb-1">
													{getEmomRoundLabel(round, j, language)}
												</div>
												<div className="space-y-0.5">
													{round.items.map((item, itemIndex) => (
														<span
															key={item.item_id || `${j}-${itemIndex}`}
															className="block text-xs text-muted-foreground"
														>
															{formatEmomItemSummary(item, language)}
														</span>
													))}
												</div>
											</div>
										))}
									</div>
								)}

								{(condType !== "emom" || emomRounds.length === 0) &&
									condExercises.length > 0 && (
										<div className="space-y-1">
											{condExercises.map((ce, j) => (
												<div
													key={j}
													className="flex items-center gap-2 text-xs text-muted-foreground"
												>
													<Dumbbell className="w-3 h-3 text-volt shrink-0" />
													<span className="truncate">
														{ce.reps && (
															<span className="font-medium text-foreground">
																{ce.reps}{" "}
															</span>
														)}
														{ce.exercise_name || ce.name}
														{ce.load && (
															<span className="text-muted-foreground">
																{" "}
																@{ce.load}
															</span>
														)}
													</span>
												</div>
											))}
										</div>
									)}

								{cfg?.conditioning_free_text && (
									<p className="text-xs text-muted-foreground whitespace-pre-line border-t border-border pt-2">
										{cfg.conditioning_free_text}
									</p>
								)}
							</div>
						);
					})()}
				</DialogContent>
			</Dialog>

			{/* Avatar crop dialog — opened when user selects a file */}
			{cropImageSrc && (
				<AvatarCropDialog
					imageSrc={cropImageSrc}
					onConfirm={handleCropConfirm}
					onCancel={() => setCropImageSrc(null)}
				/>
			)}

			{/* Sticky save bar — shown when profile has unsaved changes */}
			{/* La barra di salvataggio vale per TUTTO il profilo, non solo per il
			    tab "profilo": la classe di merito si modifica dal tab Tiro, e
			    lì non c'era modo di confermare. Si nasconde solo dove non c'è
			    niente da salvare in quella scheda. */}
			{isProfileDirty &&
				activeTab !== "notifiche" &&
				activeTab !== "salute" && (
					<div className="fixed left-0 right-0 z-[70] bg-background/95 backdrop-blur-sm border-t border-border px-4 py-3 flex items-center gap-3 bottom-[calc(52px+var(--safe-area-bottom-dynamic))] md:bottom-0">
						<Button
							onClick={handleSaveProfile}
							disabled={saving}
							className="flex-1 rounded-sm h-12 text-base font-medium bg-volt hover:bg-volt/90"
						>
							<Save className="w-4 h-4 mr-2" />
							{saving ? t("savingLabel") : t("saveProfile")}
						</Button>
					</div>
				)}
		</Layout>
	);
}
